// TODO: Add pagination (see shadcn/List.tsx for reference)
import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Grid,
  Checkbox,
  Button,
} from '@mui/material';
import { FontIcon } from './FontIcon';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { List as Interface, Component, Switch as SwitchProps, Icon as IconProps, Code as CodeProps, Text as TextProps, Color as ColorProps, Pdf as PdfProps } from '../../design/components';
import type { Property } from '../../design/types';
import { Text } from './Text';
import { Switch } from './Switch';
import { Icon } from './Icon';
import { Code } from './Code';
import { Color } from './Color';
import { Pdf } from './Pdf';
import { parseGeopoint, initLeafletIcon, calculateBounds, DEFAULT_CENTER, DEFAULT_ZOOM } from '../shared/geopoint';
import { isFileMetadata } from '../shared/image';

initLeafletIcon();

/**
 * Render a cell value using the component (inline mode).
 * All component types render with inline={true} and readonly={true}.
 */
const renderCellValue = (
  property: Property,
  component?: Component
): React.ReactNode => {
  if (!component) return '';

  const value = component.state?.[0];
  if (value === null || value === undefined) return '';

  // Render component inline based on property type
  switch (property.type) {
    case 'boolean':
      return <Switch {...component as SwitchProps} inline readonly />;
    case 'icon':
      return <Icon {...component as IconProps} inline readonly />;
    case 'code':
      return <Code {...component as CodeProps} inline readonly />;
    case 'color':
      return <Color {...component as ColorProps} inline readonly />;
    case 'pdf':
      return <Pdf {...component as PdfProps} inline readonly />;
    case 'reference':
    case 'object':
      return <List {...component as Interface} properties={property.reference?.properties || property.properties || []} inline readonly />;
    case 'custom':
      return null;
    default:
      return <Text {...component as TextProps} inline readonly />;
  }
};

interface MarkerData {
  rowIndex: number;
  coords: [number, number];
  components: Component[];
}

function FitBounds({ markers }: { markers: MarkerData[] }) {
  const map = useMap();

  React.useEffect(() => {
    if (markers.length === 0) return;

    const bounds = calculateBounds(markers.map(m => m.coords));
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [markers, map]);

  return null;
}

type ViewMode = 'table' | 'map' | 'card';

/**
 * Get a unique identifier for a row of components.
 * Uses the 'id' component's state value if available.
 */
const getRowId = (rowComponents: Component[]): string => {
  const idComponent = rowComponents.find(c => c.name === 'id');
  return String(idComponent?.state?.[0] ?? '');
};

export const List = ({
  name,
  properties,
  state,
  hidden,
  inline,
  label,
  disabled,
  readonly,
  onRowClick,
  options,
  limits,
  action,
  view,
}: Interface): React.ReactElement | null => {
  const [rawSelected, setSelectedRows] = state || [[], () => {}];
  if (rawSelected != null && !Array.isArray(rawSelected)) {
    console.warn('List: state[0] is not an array, got', typeof rawSelected, rawSelected);
  }
  const selectedRows = Array.isArray(rawSelected) ? rawSelected : [];
  const [limitError, setLimitError] = useState<string | null>(null);

  // Determine what items to display and mode
  const rows = options || selectedRows;
  const isEditMode = !!options && !readonly;

  // Selection is tracked by row IDs for efficient lookup
  const selectedIds = useMemo(
    () => new Set(selectedRows.map(row => getRowId(row))),
    [selectedRows]
  );

  const [min, max] = limits || [0, Infinity];

  const toggleItem = (rowIndex: number) => {
    const rowComponents = rows[rowIndex];
    const rowId = getRowId(rowComponents);
    setLimitError(null);

    // For max=1 (single select), set row directly instead of array
    const setValue = setSelectedRows as unknown as (v: Component[] | Component[][] | null) => void;
    if (selectedIds.has(rowId)) {
      // Deselect: for max=1, set null; otherwise filter array
      if (max === 1) {
        setValue(null);
      } else {
        setSelectedRows(selectedRows.filter(r => getRowId(r) !== rowId));
      }
    } else {
      // Select: for max=1, set single row; otherwise append to array
      if (max === 1) {
        setValue(rowComponents);
        // Auto-close when max reached
        if (action) {
          action[1]();
        }
      } else {
        const newSelection = [...selectedRows, rowComponents];
        setSelectedRows(newSelection);
        // Auto-close when max reached
        if (newSelection.length >= max && action) {
          action[1]();
        }
      }
    }
  };

  const visibleProperties = properties.filter(p => !p.hidden);
  const [viewState, setViewState] = useState<{ mode: ViewMode; modelName: string | undefined }>({
    mode: view || 'table',
    modelName: name,
  });

  // Reset view mode when model changes
  const viewMode = viewState.modelName === name ? viewState.mode : (view || 'table');
  const setViewMode = (mode: ViewMode) => setViewState({ mode, modelName: name });

  const geopointProp = useMemo(
    () => properties.find(p => p.type === 'geopoint'),
    [properties]
  );

  const imageProp = useMemo(
    () => properties.find(p => p.type === 'image'),
    [properties]
  );

  const hasAlternateViews = geopointProp || imageProp;

  const markersData = useMemo(() => {
    if (!geopointProp) return [];
    return rows
      .map((rowComponents, rowIndex) => {
        const geopointComponent = rowComponents.find(c => c.name === geopointProp.name);
        const value = geopointComponent?.state?.[0] as string | undefined;
        const coords = parseGeopoint(value);
        return coords ? { rowIndex, coords, components: rowComponents } : null;
      })
      .filter((m): m is MarkerData => m !== null);
  }, [rows, geopointProp]);

  // Inline mode: just render the label
  if (inline) {
    return <span>{label || ''}</span>;
  }

  const handleRowClick = (rowIndex: number) => {
    if (disabled) return;
    if (isEditMode) {
      toggleItem(rowIndex);
    } else {
      onRowClick?.(rowIndex);
    }
  };

  const renderTable = () => (
    <Table aria-label={name}>
      <TableHead>
        <TableRow>
          {isEditMode && <TableCell padding="checkbox" />}
          {visibleProperties.map(property => (
            <TableCell key={property.name}>
              {property.name}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={visibleProperties.length + (isEditMode ? 1 : 0)} align="center">
              <Typography color="text.secondary">No data</Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((rowComponents, rowIndex) => {
            const rowId = getRowId(rowComponents);
            const isSelected = selectedIds.has(rowId);
            return (
              <TableRow
                key={rowId || rowIndex}
                hover={!disabled}
                onClick={() => handleRowClick(rowIndex)}
                sx={{ cursor: disabled ? 'default' : 'pointer' }}
                selected={isEditMode && isSelected}
              >
                {isEditMode && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={isSelected} />
                  </TableCell>
                )}
                {visibleProperties.map(property => {
                  const component = rowComponents.find((c: Component) => c.name === property.name);
                  return (
                    <TableCell key={property.name}>
                      {renderCellValue(property, component)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const renderMap = () => {
    // Properties to show in popup (exclude image and geopoint)
    const popupProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    return (
      <Box sx={{ height: 400, width: '100%' }}>
        <MapContainer
          center={markersData.length > 0 ? markersData[0].coords : DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {markersData.map(marker => {
            const rowId = getRowId(marker.components);
            const isSelected = selectedIds.has(rowId);
            return (
              <Marker
                key={rowId || marker.rowIndex}
                position={marker.coords}
                opacity={isEditMode && !isSelected ? 0.5 : 1}
              >
                <Popup>
                  <Box sx={{ minWidth: 150 }}>
                    {isEditMode && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleItem(marker.rowIndex)}
                          size="small"
                          sx={{ p: 0, mr: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {isSelected ? 'Selected' : 'Not selected'}
                        </Typography>
                      </Box>
                    )}
                    {popupProperties.slice(0, 4).map((property, index) => {
                      const component = marker.components.find((c: Component) => c.name === property.name);
                      const value = renderCellValue(property, component);
                      return (
                        <Typography
                          key={property.name}
                          variant={index === 0 ? 'subtitle2' : 'body2'}
                          sx={{
                            fontWeight: index === 0 ? 600 : 400,
                            color: index === 0 ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {value}
                        </Typography>
                      );
                    })}
                    <Box
                      component="a"
                      onClick={() => isEditMode ? toggleItem(marker.rowIndex) : onRowClick?.(marker.rowIndex)}
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: 'primary.main',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {isEditMode ? (isSelected ? 'Deselect' : 'Select') : 'View details'}
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            );
          })}
          <FitBounds markers={markersData} />
        </MapContainer>
      </Box>
    );
  };

  const renderCards = () => {
    // Properties to show in card (exclude image and geopoint)
    const cardProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    if (rows.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No data</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        {rows.map((rowComponents, rowIndex) => {
          const rowId = getRowId(rowComponents);
          const imageComponent = imageProp
            ? rowComponents.find(c => c.name === imageProp.name)
            : undefined;
          const rawImage = imageComponent?.state?.[0];
          const imageUrl = isFileMetadata(rawImage) ? rawImage.url : undefined;
          const isSelected = selectedIds.has(rowId);

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={rowId || rowIndex}>
              <Card sx={{
                height: '100%',
                position: 'relative',
                ...(isEditMode && isSelected && {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                }),
              }}>
                {isEditMode && (
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  />
                )}
                <CardActionArea
                  onClick={() => handleRowClick(rowIndex)}
                  disabled={disabled}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
                >
                  {imageUrl && (
                    <CardMedia
                      component="img"
                      height="160"
                      image={imageUrl}
                      alt=""
                      sx={{ objectFit: 'cover', flexShrink: 0 }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, pt: 1.5 }}>
                    {cardProperties.slice(0, 4).map((property, index) => {
                      const component = rowComponents.find((c: Component) => c.name === property.name);
                      const value = renderCellValue(property, component);
                      return (
                        <Typography
                          key={property.name}
                          variant={index === 0 ? 'subtitle1' : 'body2'}
                          color={index === 0 ? 'text.primary' : 'text.secondary'}
                          sx={{
                            fontWeight: index === 0 ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {value}
                        </Typography>
                      );
                    })}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'map':
        return renderMap();
      case 'card':
        return renderCards();
      default:
        return renderTable();
    }
  };

  // Handle action click with limit validation
  const handleActionClick = () => {
    if (!action) return;
    // Check min limit when closing selection (action label is "Done")
    if (isEditMode && min > 0 && selectedRows.length < min) {
      setLimitError(`Select at least ${min} item${min > 1 ? 's' : ''}`);
      return;
    }
    setLimitError(null);
    action[1]();
  };

  const renderToolbar = () => {
    const hasToolbar = hasAlternateViews || action || limitError;
    if (!hasToolbar) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5, p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action && (
            <Button size="small" variant="contained" onClick={handleActionClick}>
              {action[0]}
            </Button>
          )}
          {limitError && (
            <Typography color="error" variant="body2">{limitError}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasAlternateViews && (
            <>
              <Tooltip title="Table view">
                <IconButton
                  onClick={() => setViewMode('table')}
                  size="small"
                  color={viewMode === 'table' ? 'primary' : 'default'}
                >
                  <FontIcon name="TableChart" />
                </IconButton>
              </Tooltip>
              {imageProp && (
                <Tooltip title="Card view">
                  <IconButton
                    onClick={() => setViewMode('card')}
                    size="small"
                    color={viewMode === 'card' ? 'primary' : 'default'}
                  >
                    <FontIcon name="ViewModule" />
                  </IconButton>
                </Tooltip>
              )}
              {geopointProp && (
                <Tooltip title="Map view">
                  <IconButton
                    onClick={() => setViewMode('map')}
                    size="small"
                    color={viewMode === 'map' ? 'primary' : 'default'}
                  >
                    <FontIcon name="Map" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  // Use TableContainer only for table view
  if (viewMode === 'table') {
    return (
      <TableContainer component={Paper} sx={{ display: hidden ? 'none' : undefined, opacity: disabled ? 0.6 : 1 }}>
        {renderToolbar()}
        {renderTable()}
      </TableContainer>
    );
  }

  // Use Box without elevation for card/map views
  return (
    <Box sx={{ display: hidden ? 'none' : undefined, opacity: disabled ? 0.6 : 1 }}>
      <Paper sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
        {renderToolbar()}
      </Paper>
      {renderContent()}
    </Box>
  );
};
